/**
 * 🔊 Notification Sound — singleton
 *
 * يشغّل صوت تنبيه عند وصول طلب حجز جديد.
 *
 * قيود المتصفح:
 *   - `audio.play()` محظور برمجياً قبل أول تفاعل من المستخدم (autoplay policy).
 *     نحلّها بـ unlockAudio() على أول نقرة/ضغطة — تُشغّل الصوت مكتوماً لحظياً
 *     لتفعيل العنصر، فتُسمح التشغيلات اللاحقة (من snapshot بلا user gesture).
 *   - تفضيل الكتم محفوظ في localStorage (محلي للجهاز، ليس في Firestore).
 */

const SOUND_URL = '/notification.wav';
const SOUND_ENABLED_KEY = 'havlaa:sound-enabled';

let audioEl: HTMLAudioElement | null = null;
let unlocked = false;

function getAudio(): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null;
  if (!audioEl) {
    audioEl = new Audio(SOUND_URL);
    audioEl.preload = 'auto';
  }
  return audioEl;
}

/**
 * 🔓 يفتح تشغيل الصوت — يُستدعى مرة واحدة على أول تفاعل للمستخدم.
 *    يُشغّل الصوت مكتوماً لحظياً ثم يوقفه، فيصير العنصر "مُفعّلاً" للتشغيل لاحقاً.
 */
export function unlockAudio(): void {
  if (unlocked) return;
  const el = getAudio();
  if (!el) return;
  const prevMuted = el.muted;
  el.muted = true;
  el
    .play()
    .then(() => {
      el.pause();
      el.currentTime = 0;
      el.muted = prevMuted;
      unlocked = true;
    })
    .catch(() => {
      el.muted = prevMuted;
      // ما نجح — نحاول مرة ثانية عند التفاعل التالي (unlocked يبقى false)
    });
}

/**
 * ▶️ يشغّل صوت التنبيه (لو الصوت مفعّل في التفضيل).
 */
export function playNotificationSound(): void {
  if (!isSoundEnabled()) return;
  const el = getAudio();
  if (!el) return;
  try {
    el.currentTime = 0;
    void el.play().catch(() => {
      // محظور (لم يُفتح الصوت بعد) — نتجاهل بصمت؛ الـ toast يظهر على أي حال
    });
  } catch {
    // تجاهل
  }
}

// ─── تفضيل الكتم (localStorage) ───

export function isSoundEnabled(): boolean {
  try {
    // مفعّل افتراضياً — نعتبره مطفأ فقط إذا حُفظ 'false' صراحةً
    return localStorage.getItem(SOUND_ENABLED_KEY) !== 'false';
  } catch {
    return true;
  }
}

export function setSoundEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(SOUND_ENABLED_KEY, enabled ? 'true' : 'false');
  } catch {
    // localStorage غير متاح — نتجاهل
  }
}
